import { addDays, format, parseISO } from 'date-fns';
import { ChangeEvent, Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IExchangeRatePeriod,
  useUpdateAutomationSettings,
} from '@legacy/apis/exchangeRateAPI';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@legacy/components/mui-components';
import { monthInNumber } from '@legacy/utils/date';
import { handleError } from '../../utils/serverResponseHandler';

interface SetupAutomationModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  latestPeriod: IExchangeRatePeriod;
  currentMode: keyof IExchangeRatePeriodPollType;
}

interface IExchangeRatePeriodPollType {
  Automatic: number;
  Manual: number;
}

export const ExchangeRatePeriodPollType: IExchangeRatePeriodPollType = {
  Automatic: 1,
  Manual: 2,
};

const SetupAutomationModal: FC<SetupAutomationModalProps> = ({
  isOpen,
  setIsOpen,
  latestPeriod,
  currentMode,
}) => {
  const { mutateAsync: updateAutoSettings } = useUpdateAutomationSettings();
  const [monthPeriod, setMonthPeriod] = useState<string>(monthInNumber[0]);
  const [selectedType, setSelectedType] = useState<number>(
    ExchangeRatePeriodPollType[currentMode],
  );
  const latestPeriodEndDate = parseISO(latestPeriod.periodEndDate);
  const nextPeriodStartDate = addDays(latestPeriodEndDate, 1);
  const nextPeriodStartDateMonthYearText = format(
    nextPeriodStartDate,
    'MMMM yyyy',
  );
  const { t } = useTranslation('systemAdminExchangeRate');

  const updateAutoSettingsAction = async () => {
    try {
      const response = await updateAutoSettings(
        {
          matrixMode: selectedType,
          periodLengthInMonth: selectedType === 1 ? Number(monthPeriod) : 0,
        },
        {
          onError: (error) => {
            handleError(error, t);
          },
        },
      );
      if (response) {
        return response;
      }
    } catch (e) {
      // TODO: We should notify the user that an error has occurred
      // for now we are just logging the error as we don't have a reasonable way to
      // tell what went wrong.
      // eslint-disable-next-line no-console
      console.error('Error updating automation settings', e);
    } finally {
      setIsOpen(false);
    }

    return null;
  };

  const onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    const type = (event.target as HTMLInputElement).value;
    setSelectedType(
      ExchangeRatePeriodPollType[type as keyof IExchangeRatePeriodPollType],
    );
  };

  const onPeriodChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setMonthPeriod((event.target as HTMLSelectElement).value);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      aria-labelledby="dialog-automation"
      aria-describedby="dialog-automation-desc"
    >
      <DialogTitle id="dialog-automation">
        {t('modals.SetupAutomationTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="dialog-automation-desc" sx={{ color: '#333' }}>
          {t('modals.SetupAutomationDescription')}
        </DialogContentText>
        <FormControl>
          <RadioGroup
            aria-labelledby="set-up-automation-label"
            name="set-up-automation-radio-group"
            defaultValue="Manual"
          >
            <FormControlLabel
              value="Automatic"
              control={
                <Radio
                  data-automation-id="AutomaticRadioButton"
                  checked={selectedType === 1}
                  onChange={(event) => onChangeValue(event)}
                />
              }
              label={t('modals.SetupAutomationAutomaticModeDescription')}
              sx={{
                marginTop: 1,
              }}
            />
            <DialogContentText
              id="dialog-manual-description"
              sx={{ marginLeft: '2rem', color: '#333' }}
            >
              {`${t('modals.SetupAutomationUsePeriodsDescriptionA')} `}
              <select
                name="setupAutomation"
                id="setupAutomationId"
                form="autoform"
                onChange={(event) => onPeriodChange(event)}
                disabled={selectedType === 2}
                data-automation-id="PeriodSelectDropdown"
              >
                {monthInNumber.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>{' '}
              {`${t(
                'modals.SetupAutomationUsePeriodsDescriptionB',
              )} ${nextPeriodStartDateMonthYearText}`}
            </DialogContentText>
            <FormControlLabel
              value="Manual"
              control={
                <Radio
                  data-automation-id="ManualRadioButton"
                  checked={selectedType === 2}
                  onChange={(event) => onChangeValue(event)}
                />
              }
              label={t('modals.SetupAutomationManualModeDescription')}
              sx={{
                marginTop: 1,
              }}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setIsOpen(false)}
          data-automation-id="SetupAutomationModalCancelButton"
        >
          {t('modals.CancelButton')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => updateAutoSettingsAction()}
          autoFocus
          data-automation-id="SetupAutomationModalSaveButton"
        >
          {t('modals.SetupAutomationSaveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetupAutomationModal;
